'use client';

import { useCallback, useEffect, useRef } from 'react';

// Animation timing constants
const DOUBLE_BLINK_DELAY_MS = 300;
const EAR_TWITCH_INTERVAL_MIN_MS = 8000;
const EAR_TWITCH_INTERVAL_RANGE_MS = 7000;
const HEAD_TILT_INTERVAL_MIN_MS = 8000;
const HEAD_TILT_INTERVAL_RANGE_MS = 4000;

export interface UseIdleAnimationsOptions {
  /** Enable idle animations like periodic blinking (default: true) */
  idleAnimations?: boolean;
  /** Blink interval range in ms [min, max] (default: [3000, 5000]) */
  blinkInterval?: [number, number];
  /** Chance of a double-blink (0-1, default: 0.2 = 20%) */
  doubleBlinkChance?: number;
  /** Trigger function for blink animation */
  triggerBlink: () => void;
  /** Trigger function for ear twitch animation */
  triggerEarTwitch: () => void;
  /** Trigger function for head tilt animation */
  triggerHeadTilt: () => void;
}

/**
 * Hook for managing Raina's idle animations.
 *
 * Schedules periodic blinking (with occasional double-blinks),
 * ear twitching, and head tilting when idle animations are enabled.
 */
export function useIdleAnimations({
  idleAnimations = true,
  blinkInterval = [3000, 5000],
  doubleBlinkChance = 0.2,
  triggerBlink,
  triggerEarTwitch,
  triggerHeadTilt,
}: UseIdleAnimationsOptions): void {
  const doubleBlinkChanceRef = useRef(doubleBlinkChance);
  doubleBlinkChanceRef.current = doubleBlinkChance;

  const blinkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const doubleBlinkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const earTwitchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headTiltTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getRandomBlinkInterval = useCallback(() => {
    const [min, max] = blinkInterval;
    return Math.random() * (max - min) + min;
  }, [blinkInterval]);

  // Idle blinking (with occasional double-blink)
  useEffect(() => {
    if (!idleAnimations) {
      if (blinkTimeoutRef.current) clearTimeout(blinkTimeoutRef.current);
      return;
    }

    let isCancelled = false;

    const scheduleBlink = () => {
      if (isCancelled) return;

      blinkTimeoutRef.current = setTimeout(() => {
        if (isCancelled) return;
        triggerBlink();

        if (Math.random() < doubleBlinkChanceRef.current) {
          doubleBlinkTimeoutRef.current = setTimeout(() => {
            if (!isCancelled) triggerBlink();
            doubleBlinkTimeoutRef.current = null;
          }, DOUBLE_BLINK_DELAY_MS);
        }

        if (!isCancelled) {
          scheduleBlink();
        }
      }, getRandomBlinkInterval());
    };

    scheduleBlink();

    return () => {
      isCancelled = true;
      if (blinkTimeoutRef.current) clearTimeout(blinkTimeoutRef.current);
      if (doubleBlinkTimeoutRef.current) clearTimeout(doubleBlinkTimeoutRef.current);
    };
  }, [idleAnimations, triggerBlink, getRandomBlinkInterval]);

  // Idle ear twitching (8–15s interval)
  useEffect(() => {
    if (!idleAnimations) {
      if (earTwitchTimeoutRef.current) clearTimeout(earTwitchTimeoutRef.current);
      return;
    }

    let isCancelled = false;

    const scheduleEarTwitch = () => {
      if (isCancelled) return;
      const interval = EAR_TWITCH_INTERVAL_MIN_MS + Math.random() * EAR_TWITCH_INTERVAL_RANGE_MS;
      earTwitchTimeoutRef.current = setTimeout(() => {
        if (isCancelled) return;
        triggerEarTwitch();
        if (!isCancelled) {
          scheduleEarTwitch();
        }
      }, interval);
    };

    scheduleEarTwitch();

    return () => {
      isCancelled = true;
      if (earTwitchTimeoutRef.current) clearTimeout(earTwitchTimeoutRef.current);
    };
  }, [idleAnimations, triggerEarTwitch]);

  // Idle head tilt (8–12s interval)
  useEffect(() => {
    if (!idleAnimations) {
      if (headTiltTimeoutRef.current) clearTimeout(headTiltTimeoutRef.current);
      return;
    }

    let isCancelled = false;

    const scheduleHeadTilt = () => {
      if (isCancelled) return;
      const interval = HEAD_TILT_INTERVAL_MIN_MS + Math.random() * HEAD_TILT_INTERVAL_RANGE_MS;
      headTiltTimeoutRef.current = setTimeout(() => {
        if (isCancelled) return;
        triggerHeadTilt();
        if (!isCancelled) {
          scheduleHeadTilt();
        }
      }, interval);
    };

    scheduleHeadTilt();

    return () => {
      isCancelled = true;
      if (headTiltTimeoutRef.current) clearTimeout(headTiltTimeoutRef.current);
    };
  }, [idleAnimations, triggerHeadTilt]);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      if (blinkTimeoutRef.current) clearTimeout(blinkTimeoutRef.current);
      if (doubleBlinkTimeoutRef.current) clearTimeout(doubleBlinkTimeoutRef.current);
      if (earTwitchTimeoutRef.current) clearTimeout(earTwitchTimeoutRef.current);
      if (headTiltTimeoutRef.current) clearTimeout(headTiltTimeoutRef.current);
    };
  }, []);
}
